import React from 'react';
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  getConfigProp,
  SearchProvider,
  SelectedSportsbookSportProvider,
  SiteConfigManager,
  useApplicationState,
} from '@atlas/core';
import { COMPONENT_TYPES, sportsbookRoutes } from '@atlas/routing';

import { useSimplifiedSportsbook } from 'common/SimplifiedSportsbook/SimplifiedSportsbookContext';
import TopEvents from 'common/Widgets/TopEvents';
import SportsbookFavorites from 'components/FavoriteEvents/SportsbookFavorites';
import FreeBetNotification from 'components/FreeBetNotification/FreeBetNotification';
import DetailsPage from 'components/Pages/Details';
import EmptySportView from 'components/Pages/EmptySportView';
import { SportsbookBanner } from 'components/Pages/Prematch/SportsbookBanner';
import {
  SelectedEventQueryRouteWrapper,
  SelectedEventQueryViewWrapper,
  SelectedProviderWrapper,
} from 'components/Pages/SelectedWrappers';
import SportItemDestruction from 'components/Pages/SportItemDestruction';
import { DefaultSportsbookNavigation } from 'components/Pages/SportsbookNavigation';
import { CategoryRedirect, SimplifiedRedirect } from 'components/Pages/SportsbookRedirects';
import { SearchInputProvider, SimpleSearchProvider } from 'components/Search';
import SportsTypeChipsList from 'components/SportTypeChipsList/SportTypeChipsList';

import SimplifiedCategoriesLoader from './SimplifiedCategoriesLoader';
import SimplifiedSelectedSportProvider from './SimplifiedSelectedSportProvider';
import SimplifiedSportsbookLoader from './SimplifiedSportsbookLoader';
import SimplifiedTournamentsView from './SimplifiedTournamentsView';

const { SPORT_PREMATCH } = COMPONENT_TYPES;

const SimplifiedSportsbookView = ({ path }) => {
  const location = useLocation();
  const { sportName } = useSimplifiedSportsbook();
  const { params } = useRouteMatch();

  const { authenticated } = useApplicationState();
  const { pathname } = location;
  const [page, locationSportName, locationCategoryName] = pathname.split('/').filter(Boolean);

  const isSportRadarProject = getConfigProp('isSportRadarProject');
  const { path: prematchPath } = SiteConfigManager.getConfig('config.pages', []).find(
    ({ type }) => type === SPORT_PREMATCH,
  );

  const isEnabledFavoritesPage = SiteConfigManager.getConfig(
    'config.config.sportsbook.enableFavouritesInSimplified',
  );

  const isBannerShow = path === prematchPath;

  const isShowFavoritesPage = authenticated && isEnabledFavoritesPage;

  const sportLivePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.SPORT_LIVE);

  const renderTopEvents = React.useMemo(() => {
    const page = SiteConfigManager.getConfig('config.pages').find(page => page.path === path);

    return (
      page.options.topEvents && (
        <div className="top-events">
          <TopEvents />
        </div>
      )
    );
  }, [path]);

  const content = (
    <SimplifiedSelectedSportProvider>
      {!params.tournamentName && <SportsTypeChipsList />}
      <DefaultSportsbookNavigation path={path}>
        <SelectedProviderWrapper Component={SelectedSportsbookSportProvider}>
          <SearchProvider>
            <SimpleSearchProvider>
              <SearchInputProvider>
                <SimplifiedSportsbookLoader>
                  {!isSportRadarProject && <FreeBetNotification />}
                  {isBannerShow && !params.tournamentName && (
                    <SportsbookBanner className="sportsbook-banner-wrapper" />
                  )}
                  {!params.tournamentName && renderTopEvents}
                  {isShowFavoritesPage && (
                    <>
                      <Route
                        component={SportsbookFavorites}
                        path={`${prematchPath}/${sportsbookRoutes.favorites}`}
                      />
                      <Route
                        component={SportsbookFavorites}
                        path={`${sportLivePath}/${sportsbookRoutes.favorites}`}
                      />
                    </>
                  )}
                  <Route
                    render={({ location }) =>
                      !(
                        isShowFavoritesPage &&
                        location.pathname.includes(sportsbookRoutes.favorites)
                      ) && (
                        <EmptySportView>
                          <SimplifiedCategoriesLoader>
                            <h1 className="d-none">
                              {page} {locationSportName} {locationCategoryName}
                            </h1>
                            <SimplifiedTournamentsView />
                          </SimplifiedCategoriesLoader>
                        </EmptySportView>
                      )
                    }
                  />
                </SimplifiedSportsbookLoader>
              </SearchInputProvider>
            </SimpleSearchProvider>
          </SearchProvider>
        </SelectedProviderWrapper>
        <Route>
          <CategoryRedirect />
          <SportItemDestruction />
        </Route>
      </DefaultSportsbookNavigation>
    </SimplifiedSelectedSportProvider>
  );

  const view = (
    <Switch>
      <Route path={`${prematchPath}/:sportName/:categoryName?/:tournamentName?`}>{content}</Route>
      <Route path={`${sportLivePath}/:sportName/:categoryName?/:tournamentName?`}>{content}</Route>
      <SimplifiedRedirect
        selectedSportName={sportName?.includes(sportsbookRoutes.favorites) ? null : sportName}
      />
    </Switch>
  );

  return (
    <SelectedEventQueryViewWrapper fallback={view} location={location}>
      <SelectedEventQueryRouteWrapper>
        <DetailsPage />
      </SelectedEventQueryRouteWrapper>
    </SelectedEventQueryViewWrapper>
  );
};

SimplifiedSportsbookView.propTypes = { path: PropTypes.string.isRequired };

export default SimplifiedSportsbookView;
